import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Container,
    Grid,
    Paper,
    Typography,
    Button,
    Box,
    Card,
    CardContent,
    Avatar,
    Alert,
    IconButton,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import { Formik, Form } from 'formik';
import { FormTextField } from '../components/common/FormFields';
import PhoneInput from '../components/common/PhoneInput';
import { countryStateConfig, ageRanges, genderOptions } from '../services/utils/lists';
import { malaysiaDistricts } from '../constants/district';
import { profileUpdateSchema } from '../services/utils/validation';
import { useAuth } from '../hooks/useAuth';
import CloseIcon from '@mui/icons-material/Close';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import type { ProfileUpdateData } from '../services/types/auth';
import api from '../services/api';

const Profile: React.FC = () => {
    const { t } = useTranslation();
    const { user, updateProfile } = useAuth();
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [allowNameFieldsEditing, setAllowNameFieldsEditing] = useState<boolean>(true);

    useEffect(() => {
        const fetchNameFieldsEditingSetting = async () => {
            try {
                if (user?.token) {
                    const response = await api.get('/admin/settings/allow-name-fields-editing', {
                        headers: {
                            'Authorization': `Bearer ${user.token}`
                        }
                    });
                    setAllowNameFieldsEditing(response.data.value === 'true');
                }
            } catch (error) {
                console.error('Error fetching name fields editing setting:', error);
                // Default to true if there's an error
                setAllowNameFieldsEditing(true);
            }
        };

        fetchNameFieldsEditingSetting();
    }, [user]);

    if (!user) {
        return null;
    }

    const initialValues: ProfileUpdateData = {
        first_name: user.first_name,
        last_name: user.last_name,
        phone: user.phone || '',
        address: user.address || '',
        city: user.city || '',
        country: user.country || '',
        age_range: user.age_range || '',
        gender: user.gender || '',
        state: user.state || '',
        school_name: user.school_name || '',
        school_district: user.school_district || ''
    };

    const handleSubmit = async (values: ProfileUpdateData, { setSubmitting }: any) => {
        try {
            setError(null);
            await updateProfile(values);
            setSuccess(t('common.profile.updateSuccess'));
            setSubmitting(false);
        } catch (err: any) {
            setError(err.response?.data?.error || t('common.profile.updateError'));
            setSubmitting(false);
        }
    };

    return (
        <Container maxWidth="lg">
            <Box sx={{ mt: 4, mb: 4 }}>
                <Grid container spacing={4}>
                    <Grid item xs={12} md={4}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center' }}>
                                {user.profile_picture ? (
                                    <Avatar
                                        src={user.profile_picture}
                                        alt={`${user.first_name} ${user.last_name}`}
                                        sx={{ width: 120, height: 120, mx: 'auto', mb: 2 }}
                                    />
                                ) : (
                                    <AccountCircleIcon
                                        sx={{ width: 120, height: 120, mx: 'auto', mb: 2, color: 'primary.main' }}
                                    />
                                )}
                                <Typography variant="h5" gutterBottom>
                                    {user.first_name} {user.last_name}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                    {user.email}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: user.is_verified ? 'success.main' : 'warning.main',
                                        mb: 2
                                    }}
                                >
                                    {user.is_verified ? t('common.profile.verifiedAccount') : t('common.profile.pendingVerification')}
                                </Typography>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="body2" color="textSecondary">
                                    {t('common.profile.role')}: {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {t('common.profile.status')}: {user.is_active ? t('common.profile.active') : t('common.profile.inactive')}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <Paper elevation={3} sx={{ p: 4 }}>
                            <Typography variant="h5" gutterBottom>
                                {t('common.profile.title')}
                            </Typography>

                            {error && (
                                <Alert
                                    severity="error"
                                    action={
                                        <IconButton
                                            aria-label="close"
                                            color="inherit"
                                            size="small"
                                            onClick={() => setError(null)}
                                        >
                                            <CloseIcon fontSize="inherit" />
                                        </IconButton>
                                    }
                                    sx={{ mb: 2 }}
                                >
                                    {error}
                                </Alert>
                            )}

                            {success && (
                                <Alert
                                    severity="success"
                                    action={
                                        <IconButton
                                            aria-label="close"
                                            color="inherit"
                                            size="small"
                                            onClick={() => setSuccess(null)}
                                        >
                                            <CloseIcon fontSize="inherit" />
                                        </IconButton>
                                    }
                                    sx={{ mb: 2 }}
                                >
                                    {success}
                                </Alert>
                            )}

                            <Formik
                                initialValues={initialValues}
                                validationSchema={profileUpdateSchema}
                                onSubmit={handleSubmit}
                            >
                                {({ isSubmitting, values, setFieldValue }) => (
                                    <Form>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} sm={6}>
                                                <FormTextField
                                                    name="first_name"
                                                    label={t('common.profile.form.firstName')}
                                                    required
                                                    fullWidth
                                                    disabled={!allowNameFieldsEditing}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormTextField
                                                    name="last_name"
                                                    label={t('common.profile.form.lastName')}
                                                    required
                                                    fullWidth
                                                    disabled={!allowNameFieldsEditing}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormControl fullWidth required>
                                                    <InputLabel id="age-range-label">{t('common.profile.form.ageRange')}</InputLabel>
                                                    <Select
                                                        labelId="age-range-label"
                                                        id="age_range"
                                                        name="age_range"
                                                        label="Age Range"
                                                        value={values.age_range || ''}
                                                        onChange={(e) => setFieldValue('age_range', e.target.value)}
                                                    >
                                                        <MenuItem value="">
                                                            <em>{t('common.profile.form.selectAgeRange')}</em>
                                                        </MenuItem>
                                                        {ageRanges.map((range) => (
                                                            <MenuItem key={range.value} value={range.value}>
                                                                {range.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormControl fullWidth required>
                                                    <InputLabel id="gender-label">{t('common.profile.form.gender')}</InputLabel>
                                                    <Select
                                                        labelId="gender-label"
                                                        id="gender"
                                                        name="gender"
                                                        label="Gender"
                                                        value={values.gender || ''}
                                                        onChange={(e) => setFieldValue('gender', e.target.value)}
                                                        disabled
                                                    >
                                                        <MenuItem value="">
                                                            <em>{t('common.profile.form.selectGender')}</em>
                                                        </MenuItem>
                                                        {genderOptions.map((option) => (
                                                            <MenuItem key={option.value} value={option.value}>
                                                                {option.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <PhoneInput
                                                    name="phone"
                                                    label={t('common.profile.form.phone')}
                                                    fullWidth
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <FormTextField
                                                    name="address"
                                                    label={t('common.profile.form.address')}
                                                    fullWidth
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormControl fullWidth required>
    <InputLabel id="country-label">{t('common.profile.form.country')}</InputLabel>
    <Select
        name="country"
        label={t('common.profile.form.country')}
        value={values.country || ''}
        onChange={(e) => {
            setFieldValue('country', e.target.value);
            setFieldValue('state', '');
        }}
    >
        <MenuItem value="">
            <em>{t('common.select')} {t('common.profile.form.country')}</em>
        </MenuItem>
        {Object.keys(countryStateConfig).map((country) => (
            <MenuItem key={country} value={country}>
                {country}
            </MenuItem>
        ))}
    </Select>
</FormControl>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormControl fullWidth required>
    <InputLabel id="state-label">{t('common.profile.form.state')}</InputLabel>
    <Select
        name="state"
        label={t('common.profile.form.state')}
        value={values.state || ''}
        onChange={(e) => setFieldValue('state', e.target.value)}
    >
        <MenuItem value="">
            <em>{t('common.select')} {t('common.profile.form.state')}</em>
        </MenuItem>
        {values.country && countryStateConfig[values.country]?.states.map((state: string) => (
            <MenuItem key={state} value={state}>
                {state}
            </MenuItem>
        ))}
    </Select>
</FormControl>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
    <FormTextField
        name="school_name"
        label={t('common.profile.form.schoolName')}
        fullWidth
        required
    />
</Box>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
    <FormControl fullWidth required>
        <InputLabel id="school-district-label">{t('common.profile.form.schoolDistrict')}</InputLabel>
        <Select
            labelId="school-district-label"
            id="school_district"
            name="school_district"
            value={values.school_district || ''}
            label={t('common.profile.form.schoolDistrict')}
            onChange={(e) => setFieldValue('school_district', e.target.value)}
            MenuProps={{ style: { maxHeight: 400 } }}
        >
            <MenuItem value="">
                <em>{t('common.profile.form.selectSchoolDistrict', 'Select School District')}</em>
            </MenuItem>
            {malaysiaDistricts.map((district: string) => (
                <MenuItem key={district} value={district}>
                    {district}
                </MenuItem>
            ))}
        </Select>
    </FormControl>
</Box>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    color="primary"
                                                    disabled={isSubmitting}
                                                    fullWidth
                                                >
                                                    {isSubmitting ? t('common.loading') : t('common.profile.form.save')}
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Form>
                                )}
                            </Formik>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        </Container>
    );
};

export default Profile;
